import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, Phone, MapPin, Save, Gamepad2, Loader2, Package, Truck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { GardienBadge } from "@/components/GardienBadge";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const SHIPPING_STYLES = {
  "à préparer": "bg-warm/15 text-warm border-warm/40",
  "en préparation": "bg-primary/10 text-primary border-primary/30",
  "expédiée": "bg-[#4A8B6F]/15 text-[#4A8B6F] border-[#4A8B6F]/40",
  "livrée": "bg-[#4A8B6F]/15 text-[#4A8B6F] border-[#4A8B6F]/40",
};

const MonCompte = () => {
  const { portalUser, setConnexionOpen, refreshPortal } = useApp();
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", postal_code: "", city: "" });

  useEffect(() => {
    if (portalUser) {
      api.get("/portal/profile").then((r) => {
        setProfile(r.data);
        setForm({
          name: r.data.name || "", phone: r.data.phone || "",
          address: r.data.address || "", postal_code: r.data.postal_code || "", city: r.data.city || "",
        });
      }).catch(() => setProfile(false));
      api.get("/portal/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    }
  }, [portalUser]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/portal/profile", form);
      await refreshPortal();
      toast.success("Vos informations ont été enregistrées");
    } catch {
      toast.error("Impossible d'enregistrer — réessayez");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, icon, placeholder, testid, type = "text") => (
    <div>
      <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-2 mb-2">{icon} {label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
      />
    </div>
  );

  return (
    <div className="pt-32 pb-24" data-testid="profile-page">
      <section className="max-w-3xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-5">Mon compte</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light leading-tight">Votre espace personnel</h1>
        </Reveal>
        <div className="mt-8">
          <GardienBadge gardienId="lucien" label="Gardien de l'espace familles" />
        </div>

        {!portalUser ? (
          <Reveal delay={0.1}>
            <div className="mt-12 bg-card rounded-3xl border border-border/50 p-10 text-center" data-testid="profile-not-logged">
              <User className="w-10 h-10 mx-auto text-primary" strokeWidth={1.4} />
              <p className="mt-5 text-foreground/70">Connectez-vous pour consulter et compléter vos informations personnelles.</p>
              <button
                onClick={() => setConnexionOpen(true)}
                data-testid="profile-login-btn"
                className="mt-6 rounded-full bg-primary text-primary-foreground px-8 py-3.5 hover:scale-105 transition-transform duration-300"
              >
                Me connecter
              </button>
            </div>
          </Reveal>
        ) : !profile ? (
          <p className="mt-12 text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement de votre profil…</p>
        ) : (
          <Reveal delay={0.1}>
            <div className="mt-12 space-y-6">
              <div className="bg-card rounded-3xl border border-border/50 p-8 sm:p-10">
                <div className="flex flex-wrap items-center gap-4 pb-8 border-b border-border/50">
                  <span className="w-14 h-14 rounded-full bg-primary/15 text-primary flex items-center justify-center font-serif-display text-2xl">
                    {(profile.name || profile.email).slice(0, 1).toUpperCase()}
                  </span>
                  <div>
                    <p className="font-serif-display text-2xl">{profile.name || "Sans nom"}</p>
                    <p className="text-sm text-muted-foreground" data-testid="profile-email">{profile.email}</p>
                  </div>
                  <span className="ml-auto text-xs uppercase tracking-[0.15em] bg-secondary/20 text-foreground/70 px-4 py-1.5 rounded-full" data-testid="profile-space">
                    Espace {profile.space === "soignant" ? "Soignant" : "Famille"}
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-8">
                  {field("name", "Nom & prénom", <User className="w-3.5 h-3.5" />, "Marie Dupont", "profile-name-input")}
                  {field("phone", "Téléphone", <Phone className="w-3.5 h-3.5" />, "06 12 34 56 78", "profile-phone-input", "tel")}
                  <div className="sm:col-span-2">
                    {field("address", "Adresse (rue)", <MapPin className="w-3.5 h-3.5" />, "12 rue des Tilleuls", "profile-address-input")}
                  </div>
                  {field("postal_code", "Code postal", <MapPin className="w-3.5 h-3.5" />, "75011", "profile-zip-input")}
                  {field("city", "Ville", <MapPin className="w-3.5 h-3.5" />, "Paris", "profile-city-input")}
                </div>

                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="profile-save-btn"
                  className="mt-8 rounded-full bg-primary text-primary-foreground px-8 py-3.5 flex items-center gap-2 hover:scale-105 transition-transform duration-300 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer mes informations
                </button>
                <p className="mt-4 text-xs text-muted-foreground">Ces informations nous aident à préparer vos commandes et à vous joindre si besoin. Votre adresse email sert d'identifiant et ne peut pas être modifiée ici.</p>
              </div>

              {profile.space === "famille" && profile.app_url && (
                <div className="bg-secondary/15 rounded-3xl border border-border/50 p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="profile-app-access">
                  <Gamepad2 className="w-10 h-10 text-primary shrink-0" strokeWidth={1.4} />
                  <div className="flex-1">
                    <h2 className="font-serif-display text-2xl">Votre application Passerelle</h2>
                    <p className="mt-2 text-sm text-foreground/70 leading-relaxed">Jeux et accompagnement cognitif — offerts aux particuliers dès 100 € d'achat.</p>
                  </div>
                  <a href={profile.app_url} target="_blank" rel="noopener noreferrer" data-testid="profile-app-btn" className="rounded-full bg-primary text-primary-foreground px-7 py-3.5 hover:scale-105 transition-transform duration-300 whitespace-nowrap">
                    Ouvrir l'application
                  </a>
                </div>
              )}

              {/* MES COMMANDES */}
              <div className="bg-card rounded-3xl border border-border/50 p-8 sm:p-10" data-testid="profile-orders">
                <div className="flex items-center gap-3 pb-6 border-b border-border/50">
                  <Package className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  <h2 className="font-serif-display text-2xl">Mes commandes</h2>
                </div>
                {orders === null ? (
                  <p className="mt-6 text-muted-foreground flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</p>
                ) : orders.length === 0 ? (
                  <div className="mt-6 text-center py-8" data-testid="orders-empty">
                    <p className="text-foreground/70">Aucune commande pour le moment.</p>
                    <Link to="/boutique" data-testid="orders-shop-link" className="inline-block mt-4 rounded-full bg-primary text-primary-foreground px-7 py-3 text-sm hover:scale-105 transition-transform duration-300">
                      Découvrir la boutique
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-border/50" data-testid="orders-list">
                    {orders.map((o) => (
                      <div key={o.id} className="py-5" data-testid={`order-${o.id}`}>
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-sm text-muted-foreground">{o.date}</span>
                          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{o.provider === "paypal" ? "PayPal" : "Carte bancaire"}</span>
                          <span className="font-serif-display text-xl ml-auto">{Number(o.amount).toFixed(2)} €</span>
                        </div>
                        <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                          {o.items.map((it) => `${it.name} × ${it.quantity}${it.familiar ? ` (${it.familiar})` : ""}`).join(" · ")}
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          {o.refunded ? (
                            <span className="text-xs px-3.5 py-1.5 rounded-full border bg-muted text-muted-foreground border-border" data-testid={`order-status-${o.id}`}>Remboursée</span>
                          ) : (
                            <span className={`text-xs px-3.5 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${SHIPPING_STYLES[o.shipping_status] || "bg-muted text-muted-foreground border-border"}`} data-testid={`order-status-${o.id}`}>
                              <Truck className="w-3.5 h-3.5" /> {o.shipping_status}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        )}
      </section>
    </div>
  );
};

export default MonCompte;
